import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";

import { RefusalError, setApiSpace, setCsrfToken } from "../api/client";
import { isAuthProblem } from "../api/errors";
import type { MeView } from "../api/types";

/**
 * The query client of the app.
 *
 * - The CSRF token and the space (discipline or practice) always follow the `me` view, wherever that view
 *   comes from (a fetch, the answer of sign-in, or a switch of discipline).
 * - A refusal "not signed in" or "change your password first" from any other request sends the page
 *   back to `me`, which sends the person to the right screen.
 * - A refusal is an answer, not a failure: it is not tried again.
 */
export function makeQueryClient(): QueryClient {
  const backToMe = (error: unknown, key?: unknown) => {
    if (key !== "me" && isAuthProblem(error)) void client.invalidateQueries({ queryKey: ["me"] });
  };

  const client: QueryClient = new QueryClient({
    queryCache: new QueryCache({ onError: (error, query) => backToMe(error, query.queryKey[0]) }),
    mutationCache: new MutationCache({ onError: (error) => backToMe(error) }),
    defaultOptions: {
      queries: {
        retry: (failures, error) => !(error instanceof RefusalError) && failures < 1,
        refetchOnWindowFocus: false,
      },
    },
  });

  client.getQueryCache().subscribe((event) => {
    if (event.query.queryKey[0] !== "me") return;
    if (event.type === "removed") {
      setCsrfToken(null);
      setApiSpace("workspace");
    } else if (event.type === "updated" && event.action.type === "success") {
      const me = event.query.state.data as MeView | undefined;
      setCsrfToken(me?.csrf_token ?? null);
      setApiSpace(me?.active?.space ?? "workspace");
    }
  });

  return client;
}
